/**
 * @module support/shared
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
import { Injectable } from '@angular/core';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import {
  HardgoodDataInterface,
  HardgoodDataProperty,
} from '@support/shared/interfaces/hardgood-data.interface';
import { CommonUtilService } from '@support/shared/services/common-util.service';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';

/** */

@Injectable(
  { providedIn: 'root' }
)
export class HardgoodsDataService {
  constructor(
    private stateStoreUtils: StateStoreUtilsService,
    private commonUtilsService: CommonUtilService,
    private objectService: ObjectService
  ) {}
  /**
   * method to set HardGoods Data in state store
   * @param sku: sku of product
   * @param data: Object of type HardgoodDataProperty
   * @returns void
   */

  public updateHardgoodData(sku: string, data: HardgoodDataProperty): void {
    const currentCachedHardgoodMap = this.stateStoreUtils.getHardGoodsCachedData();
    if (this.objectService.get(currentCachedHardgoodMap, 'mappedData')) {
      this.stateStoreUtils.setHardGoodsCachedData({
        mappedData: currentCachedHardgoodMap.mappedData.set(sku, data),
      });
    } else {
      this.stateStoreUtils.setHardGoodsCachedData({ mappedData: new Map().set(sku, data) });
    }
  }

  /**
   * method to get HardGoods Data in state store against sku
   * @param sku: sku of product
   * @returns HardgoodDataProperty
   */
  public getHardgoodDataForSku(sku: string): HardgoodDataProperty {
    const savedData: HardgoodDataInterface = this.stateStoreUtils.getHardGoodsCachedData();
    if (savedData) {
      if (savedData.mappedData && savedData.mappedData.get(sku)) {
        return savedData.mappedData.get(sku);
      } else {
        return this.getDefaultHardgoodProperty();
      }
    } else {
      return this.getDefaultHardgoodProperty();
    }
  }
  /**
   * method to get HardGoods Data Map for all sku from state store
   * @returns HardgoodDataInterface
   */
  public getHardGoodDataMap(): HardgoodDataInterface {
    return this.stateStoreUtils.getHardGoodsCachedData();
  }
  /**
   * method to get Hdefault Hardgood map
   * @returns HardgoodDataInterface
   */
  public getDefaultHardgoodDataMap(): HardgoodDataInterface {
    return { mappedData: new Map() };
  }
  /**
   * method to get Product Image
   * @param sku : Sku of the product
   * @param inputImageUrl: optional input if we have image in cart
   * @returns string : Fully formed URL of the image
   */
  public getProductImageIfNotInCart(sku: string, inputImageURL?: string): string {
    if (this.commonUtilsService.isNotEmpty(inputImageURL)) {
      return this.commonUtilsService.getImageUrl(inputImageURL);
    } else {
      const imageDetails = this.getHardgoodDataForSku(sku);
      if (imageDetails && this.commonUtilsService.isNotEmpty(imageDetails.productImageURL)) {
        return this.commonUtilsService.getImageUrl(imageDetails.productImageURL);
      } else {
        return this.commonUtilsService.getImageUrl('');
      }
    }
  }
  /**
   * method to get default Hardgood properties
   * @returns HardgoodDataProperty
   */
  private getDefaultHardgoodProperty(): HardgoodDataProperty {
    // In future we can set a default image here. Or URL of image not found
    return { productImageURL: '' };
  }
}
